import { fabric } from 'fabric';
import { matchWeight, weightOf } from '../print/rules';
import { FONT_CATALOG, fontInfo, THAI_FALLBACK } from './catalog';

/*
 * Fonts on screen.
 *   installFontFaces()  once at start: @font-face for the local fonts (/fonts/*.woff2) and, for every font without
 *                       Thai letters, a face of the same name that covers only Thai (U+0E00–0E7F) with THAI_FALLBACK.
 *                       The saved work keeps the font's own name; the browser picks per letter.
 *   loadFonts()         loads what some objects use before they are measured or drawn — on opening a work, before
 *                       a picture/PDF/video is made, when a font is picked. A font that cannot be loaded is reported,
 *                       never silently swapped for another (strict: refused; otherwise: named for a message).
 */

const TEXT_TYPES = new Set(['thaiTextbox', 'textbox', 'i-text', 'text']);
const THAI_RANGE = 'U+0E00-0E7F';
const NOT_THAI = 'U+0000-0DFF, U+0E80-10FFFF';
const SAMPLE = 'กขคAa';

const face = (family: string, url: string, weight: number, range: string) =>
  `@font-face{font-family:"${family}";src:url(${url}) format("woff2");font-weight:${weight};font-style:normal;font-display:swap;unicode-range:${range}}`;

/** the @font-face rules for the local fonts and the Thai fallback faces */
export function fontFaceCss(): string {
  const fb = fontInfo(THAI_FALLBACK)!;
  const out: string[] = [];
  for (const f of FONT_CATALOG) {
    if (f.source === 'local') for (const w of f.weights) out.push(face(f.family, `/fonts/${f.slug}-${w}.woff2`, w, f.thai ? 'U+0000-10FFFF' : NOT_THAI));
    // only at the font's own weights: a 700 Thai-only face for a 400-only font would win the weight match for bold
    // text and leave its English letters to the system font
    if (!f.thai) for (const w of f.weights) out.push(face(f.family, `/fonts/${fb.slug}-${Math.min(w, 700)}.woff2`, w, THAI_RANGE));
  }
  return out.join('\n');
}

export function installFontFaces(): void {
  if (document.getElementById('fontFaces')) return;
  const s = document.createElement('style');
  s.id = 'fontFaces';
  s.textContent = fontFaceCss();
  document.head.append(s);
}

export interface FontNeed { family: string; weight: number }

/** the fonts (family + the weight actually drawn) some objects use */
export function fontNeeds(objects: Array<Record<string, unknown>>): FontNeed[] {
  const want = new Map<string, FontNeed>();
  // the text inside groups too (ตาราง, src/table/tableFabric.ts: a Thai text box per cell)
  const all: Array<Record<string, unknown>> = [];
  const walk = (list: unknown) => {
    if (!Array.isArray(list)) return;
    for (const o of list) if (o && typeof o === 'object') { all.push(o as Record<string, unknown>); walk((o as { objects?: unknown }).objects); }
  };
  walk(objects);
  for (const o of all) {
    if (!TEXT_TYPES.has(String(o.type))) continue;
    const family = String(o.fontFamily ?? 'Sarabun').replace(/^["']|["']$/g, '');
    const info = fontInfo(family);
    const weight = info ? matchWeight(info.weights, weightOf(o.fontWeight)) : (weightOf(o.fontWeight) >= 600 ? 700 : 400);
    want.set(`${weight}|${family}`, { family, weight });
  }
  return [...want.values()];
}

/** Loads the fonts; returns the names that could not be loaded (empty = all there). */
/**
 * Is the font really there? `document.fonts.check()` alone answers yes when the browser has NO face of that name at
 * all (the spec: "true if no font matches") — so with the Google Fonts stylesheet blocked or offline, every Google
 * font looked loaded and the text was drawn and exported with a stand-in, silently (BUG-78). A font is there when
 * loading it found a face (`loaded`, what document.fonts.load() returns) and check() agrees.
 */
export const fontReady = (loaded: readonly unknown[], checked: boolean) => loaded.length > 0 && checked;

/**
 * The Google Fonts stylesheet (index.html <link>), waited for once: its faces exist only after it has come. If it
 * could not be fetched there are no faces for those fonts — fontReady() then says so.
 */
let googleCss: Promise<void> | null = null;
function googleSheet(): Promise<void> {
  googleCss ??= new Promise<void>(res => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="stylesheet"][href*="fonts.googleapis.com"]');
    // there already (loaded: a sheet · failed: the page finished without one)
    if (!link || link.sheet || document.readyState === 'complete') { res(); return; }
    const done = () => res();
    link.addEventListener('load', done, { once: true });
    link.addEventListener('error', done, { once: true });
    window.addEventListener('load', done, { once: true });
  });
  return googleCss;
}

export async function loadFonts(needs: FontNeed[]): Promise<string[]> {
  const failed: string[] = [];
  await googleSheet();
  await Promise.all(needs.map(async n => {
    const spec = `${n.weight} 40px "${n.family}"`;
    let loaded: readonly unknown[] = [];
    try { loaded = await document.fonts.load(spec, SAMPLE); } catch { /* a face that could not be fetched: not ready */ }
    if (!fontReady(loaded, document.fonts.check(spec, SAMPLE))) failed.push(n.family);
    // Fabric keeps letter widths per font name: widths measured with a stand-in before the font came must go,
    // or pictures/PDF/video made later lay the text out with the stand-in's widths
    fabric.util.clearFabricFontCache(n.family);
  }));
  return [...new Set(failed)];
}

/** For making files (pictures, PDF, video): every font must be there, or nothing is made — with the reason. */
export async function requireFonts(objects: Array<Record<string, unknown>>): Promise<void> {
  const failed = await loadFonts(fontNeeds(objects));
  if (failed.length) {
    throw new Error(`โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตรวจอินเทอร์เน็ตแล้วลองใหม่ (ถ้าเน็ตปกติ เครือข่ายนี้อาจบล็อก Google Fonts) ระบบไม่ใช้ฟอนต์สำรองแทน เพื่อไม่ให้ตัวหนังสือเพี้ยน`);
  }
}

/**
 * On the editing canvas: load the fonts its text uses, then measure that text again (it may have been laid out with
 * a stand-in while the font was coming). Returns the names that could not be loaded.
 */
export async function fitCanvasFonts(canvas: fabric.Canvas, objects: fabric.Object[]): Promise<string[]> {
  const texts = objects.filter(o => TEXT_TYPES.has(o.type ?? ''));
  if (!texts.length) return [];
  const needs = fontNeeds(texts as unknown as Array<Record<string, unknown>>);
  const failed = await loadFonts(needs);
  for (const n of needs) fabric.util.clearFabricFontCache(n.family);
  for (const o of texts) {
    if (!canvas.getObjects().includes(o)) continue;   // gone meanwhile (another page or work opened)
    (o as fabric.Textbox).initDimensions();
    o.setCoords();
  }
  canvas.requestRenderAll();
  return failed;
}
